import { useEffect, useMemo, useRef, useState } from "react";
import {
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
  CardNumberElementProps,
  CardExpiryElementProps,
  CardCvcElementProps,
} from "@stripe/react-stripe-js";
import { StripeElementChangeEvent } from "@stripe/stripe-js";

import { useClickOutside, useFormState, useSingleFlight } from "common/hooks";
import { Optional } from "common/utils/types";
import { FetchError } from "common/fetch/errors";
import { deepCombineErrors } from "common/utils/errors";
import { isNumericOnlyName, NUMERIC_ONLY_NAME_ERROR } from "common/utils/payments";
import { CardPaymentDeclineError, Feature, StripeCardDeclineCode } from "common/types";
import { useClinicGroupCountry } from "common/contexts/UIContext";
import { useConfirmPaymentElementSetupMutation } from "common/stripe/hooks";
import { paymentElementAppearanceBase } from "common/stripe/appearance";

import TextInput from "client/common/components/inputs/TextInput";
import { stripeElementsOptions, stripePromise } from "client/common/contexts/stripe";
import { useSetupIntentMutation, useStripeCardDeclineCodesQuery } from "client/payments/hooks";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import ErrorResults from "client/common/components/results/ErrorResults";
import BaseInput from "client/common/components/inputs/BaseInput";
import ErrorMessage from "client/common/components/errors/ErrorMessage";
import CardPaymentDeclineAlert from "client/common/components/content/CardPaymentDeclineAlert";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useFeatures } from "client/clinics/hooks";

import { PaymentMethodCreate } from "../types";

import tokens from "common/styles/tokens/exports.less?export";
import styleVars from "client/common/styles/export-variables.less?export";
import styles from "../styles/PaymentForm.module.less";

const PAYMENT_ELEMENT_LOAD_FALLBACK_MS = 3000;

const stripeInputOptions = {
  style: {
    // input styles must be directly provided here
    // otherwise defaults set by Stripe will override our classes
    base: {
      fontSmoothing: "antialiased",
      fontFamily: styleVars.fontFamilyDirect,
      fontWeight: styleVars.fontWeight,
      fontSize: styleVars.fontSize,
      color: styleVars.textPrimaryColor,
    },
  },
  // Disable placeholder since we render a label above the input
  placeholder: "",
};

// Matches @input-box-shadow (@box-shadow[200] over @grey[700]). Colors must
// use legacy rgba() syntax: Stripe's appearance parser silently drops values
// written in modern space-separated rgb() syntax.
const inputBoxShadow = "0 1px 3px 0 rgba(51, 65, 85, 0.1), 0 1px 2px -1px rgba(51, 65, 85, 0.1)";
// Matches .input-focus(): the grey-600 border is drawn as an outer ring so it
// renders even when Stripe's focused-state styles override border-color
const focusBoxShadow = `${inputBoxShadow}, 0 0 0 0.1px ${tokens["grey-600"]}, 0 0 0 0.5px ${tokens["grey-600"]} inset`;

const paymentElementAppearance = {
  ...paymentElementAppearanceBase,
  variables: {
    ...paymentElementAppearanceBase.variables,
    fontFamily: styleVars.fontFamilyDirect,
    fontSizeBase: styleVars.fontSize,
    fontWeightNormal: styleVars.fontWeight,
    borderRadius: tokens["border-radius-s"],
    // Suppress Stripe's brand-colored focus ring; focus styling is applied
    // via box shadows below
    focusBoxShadow: "none",
    focusOutline: "none",
    inputFocusBoxShadow: focusBoxShadow,
  },
  rules: {
    ...paymentElementAppearanceBase.rules,
    // Resting floating labels match the client BaseInput label color
    ".Label": {
      color: tokens["grey-600"],
      fontWeight: styleVars.fontWeight,
    },
    ".Label--floating": {
      color: tokens["grey-600"],
      fontSize: tokens["font-size-xs"],
    },
    // Match the client BaseInput controls (.input-default / .input-text).
    // The floating label stacks above the value at a fixed core height, so
    // vertical padding brings the control up to BaseInput's rendered height.
    ".Input": {
      backgroundColor: tokens.white,
      border: `1px solid ${tokens["grey-200"]}`,
      borderRadius: tokens["border-radius-s"],
      boxShadow: inputBoxShadow,
      color: tokens["grey-800"],
      fontSize: styleVars.fontSize,
      fontWeight: styleVars.fontWeight,
      lineHeight: "1.5",
      outline: "none",
      padding: `${tokens["spacing-s"]} ${tokens["spacing-m"]}`,
      transition: "none",
    },
    // The hover rule must set boxShadow explicitly: the flat theme's hover
    // style clears shadows, which would wipe the focus ring while the
    // pointer is over a focused field
    ".Input:hover": {
      borderColor: tokens["grey-300"],
      boxShadow: inputBoxShadow,
    },
    ".Input:focus": {
      borderColor: tokens["grey-600"],
      boxShadow: focusBoxShadow,
    },
    // Keeps the focus ring visible while hovering a focused field; without
    // this the :hover boxShadow above would override it
    ".Input:focus:hover": {
      borderColor: tokens["grey-600"],
      boxShadow: focusBoxShadow,
    },
    ".Input--invalid": {
      borderColor: tokens["danger-200"],
      boxShadow: `${inputBoxShadow}, 0 0 0 0.5px ${tokens["danger-200"]} inset`,
    },
    // Legal fine print matches the app's small secondary text
    ".TermsText": {
      color: tokens["grey-600"],
      fontSize: tokens["font-size-s"],
      lineHeight: "1.43",
    },
  },
} as const;

const PaymentInformationLoadError = () => {
  return (
    <DrawerLayout.Error
      title="Oh no!"
      description="It looks like there was a problem loading payment information. Please try refreshing your browser or check your internet connection to continue."
      actions={[{ label: "Refresh page", onClick: () => window.location.reload() }]}
    />
  );
};

enum CardElement {
  Number = "NUMBER",
  Expiry = "EXPIRTY",
  Cvc = "CVC",
}

const exhaustiveCheck = (element: never, entity: string) => {
  console.warn(`Missing ${entity} for ${element}`);
};

const getStripeElement = (
  element: CardElement,
  props: CardNumberElementProps | CardExpiryElementProps | CardCvcElementProps,
): React.ReactNode => {
  switch (element) {
    case CardElement.Number:
      return <CardNumberElement {...(props as CardNumberElementProps)} />;
    case CardElement.Expiry:
      return <CardExpiryElement {...(props as CardExpiryElementProps)} />;
    case CardElement.Cvc:
      return <CardCvcElement {...(props as CardCvcElementProps)} />;
    default:
      exhaustiveCheck(element, "Stripe element definition");
      return null;
  }
};

const getLabelForElement = (element: CardElement) => {
  switch (element) {
    case CardElement.Number:
      return "Card Number";
    case CardElement.Expiry:
      return "MM/YY";
    case CardElement.Cvc:
      return "CVC";
    default:
      exhaustiveCheck(element, "label");
      return "";
  }
};

type StripeInputProps = {
  element: CardElement;
  isFocused: boolean;
  onFocus: () => void;
  onBlur: () => void;
  onChange: (e: StripeElementChangeEvent) => void;
  className: string;
  inputClassName: string;
};

const StripeInput = ({
  element,
  isFocused,
  onFocus,
  onBlur,
  onChange: onChangeProp,
  className,
  inputClassName,
}: StripeInputProps) => {
  const [isEmpty, setIsEmpty] = useState(true);

  const onChange = (e: StripeElementChangeEvent) => {
    setIsEmpty(e.empty);
    onChangeProp(e);
  };

  const props = {
    options: stripeInputOptions,
    onFocus,
    onBlur,
    onChange,
    className: inputClassName,
  };

  return (
    <BaseInput.AnimationContainer
      className={className}
      label={getLabelForElement(element)}
      isActive={isFocused || !isEmpty}
    >
      {/* Eslint doesn't realize that the stripe inputs are inputs */}
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label>{getStripeElement(element, props)} </label>
    </BaseInput.AnimationContainer>
  );
};

type FormValues = {
  name: string;
  zipcode: string;
};

type PaymentFormProps = {
  initialValues?: FormValues;
  onSubmit: (paymentMethodId: string) => void;
  isLoading: boolean;
  error: Optional<FetchError<PaymentMethodCreate>>;
};

type PaymentFormContentProps = PaymentFormProps & {
  clientSecret: string;
  stripeCardDeclineCodes: StripeCardDeclineCode[];
};

const PaymentFormContent = ({
  clientSecret,
  stripeCardDeclineCodes,
  initialValues,
  onSubmit,
  isLoading: isLoadingProp,
  error: errorProp,
}: PaymentFormContentProps) => {
  const stripe = useStripe();
  const elements = useElements();

  const [focusedElement, setFocusedElement] = useState<CardElement | undefined>();
  const [isNumberComplete, setIsNumberComplete] = useState(false);
  const [isExpiryComplete, setIsExpiryComplete] = useState(false);
  const [isCvcComplete, setIsCvcComplete] = useState(false);
  const isComplete = isNumberComplete && isExpiryComplete && isCvcComplete;

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const [cardDeclineError, setCardDeclineError] = useState<CardPaymentDeclineError | undefined>(
    undefined,
  );

  const blurRef = useRef<HTMLDivElement>(null);
  useClickOutside(blurRef, () => setFocusedElement(undefined));

  const [values, updateField] = useFormState<FormValues>({
    name: initialValues?.name ?? "",
    zipcode: initialValues?.zipcode ?? "",
  });

  useEffect(() => {
    const focusExpiry = () => {
      setFocusedElement(CardElement.Expiry);
      elements?.getElement(CardExpiryElement)?.focus();
    };
    const focusCvc = () => {
      setFocusedElement(CardElement.Cvc);
      elements?.getElement(CardCvcElement)?.focus();
    };

    if (isNumberComplete && focusedElement === CardElement.Number) {
      if (isExpiryComplete) {
        focusCvc();
      } else {
        focusExpiry();
      }
    }

    if (isExpiryComplete && focusedElement === CardElement.Expiry) {
      focusCvc();
    }
    // Don't run when focusedElement changes so user can still manually focus completed inputs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements, isNumberComplete, isExpiryComplete]);

  const handleStripeBlur = () => {
    window.setTimeout(() => {
      if (!blurRef.current?.contains(document.activeElement)) {
        setFocusedElement(undefined);
      }
    }, 0);
  };

  const onNumberChange = (e: StripeElementChangeEvent) => {
    setCardDeclineError(undefined);
    setIsNumberComplete(e.complete);
    setError(e.error?.message ?? "");
  };

  const onExpiryChange = (e: StripeElementChangeEvent) => {
    setCardDeclineError(undefined);
    setIsExpiryComplete(e.complete);
    setError(e.error?.message ?? "");
  };

  const onCvcChange = (e: StripeElementChangeEvent) => {
    setCardDeclineError(undefined);
    setIsCvcComplete(e.complete);
    setError(e.error?.message ?? "");
  };

  const handleSubmit = useSingleFlight(async () => {
    // Only card number element is needed, it will have the expiry/cvc data
    const cardNumber = elements?.getElement(CardNumberElement);

    if (!stripe || !elements || !cardNumber) {
      return;
    }

    setIsProcessing(true);

    let payload: Awaited<ReturnType<typeof stripe.confirmCardSetup>>;
    try {
      payload = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardNumber,
          billing_details: {
            name: values.name,
            address: {
              postal_code: values.zipcode,
            },
          },
        },
      });
    } catch {
      // confirmCardSetup normally resolves with `payload.error`; a rejection
      // (e.g. network failure) would otherwise leave the form silently reset
      setCardDeclineError(undefined);
      setError("An error occurred verifying this card.");
      return;
    } finally {
      setIsProcessing(false);
    }

    const declineCodeInfo = stripeCardDeclineCodes.find(
      (declindeCodeDescriptor: StripeCardDeclineCode) =>
        declindeCodeDescriptor.decline_code === payload.error?.decline_code,
    );

    if (declineCodeInfo) {
      setError("");
      setCardDeclineError({
        card_decline_description: declineCodeInfo.description,
        card_decline_next_steps: declineCodeInfo.next_steps,
      });
    } else if (payload.error) {
      setCardDeclineError(undefined);
      setError(payload.error?.message ?? "An error occurred verifying this card.");
    } else if (payload.setupIntent?.payment_method) {
      const paymentMethod = payload.setupIntent.payment_method;
      onSubmit(typeof paymentMethod === "string" ? paymentMethod : paymentMethod.id);
    }
  });

  const isLoading = isProcessing || isLoadingProp;
  const nameError = isNumericOnlyName(values.name) ? NUMERIC_ONLY_NAME_ERROR : undefined;
  const canSubmit = !!(isComplete && values.name && values.zipcode && !nameError && !isLoading);

  return (
    <DrawerLayout.Form
      submitText="Add payment method"
      canSubmit={canSubmit}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      // There is not a 1:1 mapping from request to fields, so we collapse any reported field errors
      error={
        errorProp && {
          ...errorProp,
          errors: {
            ...errorProp.errors,
            non_field_errors: deepCombineErrors(errorProp.errors),
          },
        }
      }
    >
      <DrawerLayout.Form.Group align="horizontal">
        <TextInput
          value={values.name}
          label="Name"
          onChange={updateField("name")}
          errors={nameError ? [nameError] : undefined}
          className={styles.wideInput}
        />
        <TextInput
          value={values.zipcode}
          label="Zipcode"
          onChange={updateField("zipcode")}
          className={styles.normalInput}
          maxLength={5}
        />
      </DrawerLayout.Form.Group>
      <DrawerLayout.Form.Group>
        <BaseInput
          value=""
          onChange={() => null}
          useLabelWrapper={false}
          styles={{
            content: styles.cardInputContent,
          }}
          errors={error ? [error] : undefined}
          isFocused={!!focusedElement}
          renderInputDisplay={({ className }) => (
            <div className={styles.cardInput} ref={blurRef}>
              <StripeInput
                element={CardElement.Number}
                isFocused={focusedElement === CardElement.Number}
                onFocus={() => setFocusedElement(CardElement.Number)}
                onBlur={handleStripeBlur}
                onChange={onNumberChange}
                className={styles.wideInput}
                inputClassName={className}
              />
              <StripeInput
                element={CardElement.Expiry}
                isFocused={focusedElement === CardElement.Expiry}
                onFocus={() => setFocusedElement(CardElement.Expiry)}
                onBlur={handleStripeBlur}
                onChange={onExpiryChange}
                className={styles.normalInput}
                inputClassName={className}
              />
              <StripeInput
                element={CardElement.Cvc}
                isFocused={focusedElement === CardElement.Cvc}
                onFocus={() => setFocusedElement(CardElement.Cvc)}
                onBlur={handleStripeBlur}
                onChange={onCvcChange}
                className={styles.normalInput}
                inputClassName={className}
              />
            </div>
          )}
        />
        {cardDeclineError && <CardPaymentDeclineAlert declineError={cardDeclineError} />}
      </DrawerLayout.Form.Group>
    </DrawerLayout.Form>
  );
};

const PaymentElementFormContent = ({
  stripeCardDeclineCodes,
  initialValues,
  onSubmit,
  isLoading: isLoadingProp,
  error: errorProp,
}: PaymentFormContentProps) => {
  const clinicGroupCountry = useClinicGroupCountry();

  const [isComplete, setIsComplete] = useState(false);
  // The PaymentElement iframe loads asynchronously; the form stays hidden
  // behind the loading state until it's ready so all fields appear together
  const [isElementReady, setIsElementReady] = useState(false);
  const [hasElementLoadFailed, setHasElementLoadFailed] = useState(false);
  const {
    mutateAsync: confirmPaymentElementSetup,
    isPending: isConfirmingSetup,
    error: paymentElementSetupError,
    reset: resetPaymentElementSetup,
  } = useConfirmPaymentElementSetupMutation();

  const [values, updateField] = useFormState<FormValues>({
    name: initialValues?.name ?? "",
    zipcode: initialValues?.zipcode ?? "",
  });

  useEffect(() => {
    if (isElementReady || hasElementLoadFailed) {
      return undefined;
    }

    const timer = window.setTimeout(
      () => setHasElementLoadFailed(true),
      PAYMENT_ELEMENT_LOAD_FALLBACK_MS,
    );
    return () => window.clearTimeout(timer);
  }, [hasElementLoadFailed, isElementReady]);

  const handleSubmit = useSingleFlight(async () => {
    try {
      const paymentMethodId = await confirmPaymentElementSetup({
        billingDetails: {
          // The ZIP code is collected by the PaymentElement itself; only the
          // hidden fields (name, country) need to be provided here
          name: values.name,
          address: {
            country: clinicGroupCountry,
          },
        },
      });
      onSubmit(paymentMethodId);
    } catch {
      // The error is surfaced via paymentElementSetupError; swallow the
      // rejection so the guard resets and the customer can retry.
    }
  });

  const declineCodeInfo = stripeCardDeclineCodes.find(
    (declineCodeDescriptor: StripeCardDeclineCode) =>
      declineCodeDescriptor.decline_code === paymentElementSetupError?.decline_code,
  );
  const cardDeclineError = declineCodeInfo
    ? {
        card_decline_description: declineCodeInfo.description,
        card_decline_next_steps: declineCodeInfo.next_steps,
      }
    : undefined;
  const error =
    !cardDeclineError && paymentElementSetupError ? paymentElementSetupError.message : "";
  const isLoading = isConfirmingSetup || isLoadingProp;
  const nameError = isNumericOnlyName(values.name) ? NUMERIC_ONLY_NAME_ERROR : undefined;
  const canSubmit = !!(isComplete && values.name && !nameError && !isLoading);

  if (hasElementLoadFailed) {
    return <PaymentInformationLoadError />;
  }

  return (
    <>
      {!isElementReady && <LoadingContainer />}
      <div className={isElementReady ? undefined : styles.hiddenWhileElementLoads}>
        <DrawerLayout.Form
          submitText="Add payment method"
          canSubmit={canSubmit}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          error={
            errorProp && {
              ...errorProp,
              errors: {
                ...errorProp.errors,
                non_field_errors: deepCombineErrors(errorProp.errors),
              },
            }
          }
        >
          <DrawerLayout.Form.Group>
            <TextInput
              value={values.name}
              label="Name"
              onChange={updateField("name")}
              errors={nameError ? [nameError] : undefined}
              className={styles.wideInput}
            />
          </DrawerLayout.Form.Group>
          <DrawerLayout.Form.Group>
            <div className={styles.paymentElement}>
              <PaymentElement
                options={{
                  layout: "tabs",
                  defaultValues: {
                    billingDetails: {
                      address: { postal_code: initialValues?.zipcode ?? "" },
                    },
                  },
                  fields: {
                    billingDetails: {
                      name: "never",
                      address: {
                        country: "never",
                      },
                    },
                  },
                }}
                onReady={() => setIsElementReady(true)}
                onLoadError={() => setHasElementLoadFailed(true)}
                onChange={(event) => {
                  setIsComplete(event.complete);
                  if (paymentElementSetupError) {
                    resetPaymentElementSetup();
                  }
                }}
              />
              {!!error && <ErrorMessage>{error}</ErrorMessage>}
            </div>
            {cardDeclineError && <CardPaymentDeclineAlert declineError={cardDeclineError} />}
          </DrawerLayout.Form.Group>
        </DrawerLayout.Form>
      </div>
    </>
  );
};

const PaymentElementForm = (props: PaymentFormContentProps) => {
  return <PaymentElementFormContent {...props} />;
};

const PaymentForm = (props: PaymentFormProps) => {
  const stripe = useStripe();
  const isPaymentElementEnabled = useFeatures()[Feature.PaymentElementHD];
  const {
    data: stripeCardDeclineCodes,
    isLoading: isStripeCardDeclineCodesLoading,
    error: stripeCardDeclineCodeFetchError,
  } = useStripeCardDeclineCodesQuery();
  const {
    mutate: genSetupIntent,
    isPending: isSetupLoading,
    error: setupIntentError,
    data: setupIntent,
  } = useSetupIntentMutation();

  useEffect(() => {
    if (stripe && !setupIntent) {
      genSetupIntent();
    }
  }, [stripe, setupIntent, genSetupIntent]);

  const elementOptions = useMemo(
    () =>
      setupIntent
        ? {
            ...stripeElementsOptions,
            appearance: paymentElementAppearance,
            clientSecret: setupIntent.client_secret,
          }
        : undefined,
    [setupIntent],
  );

  if (!stripe) {
    return <PaymentInformationLoadError />;
  }

  if (isSetupLoading || isStripeCardDeclineCodesLoading) {
    return <LoadingContainer />;
  }

  if (
    !setupIntent ||
    setupIntentError ||
    !stripeCardDeclineCodes ||
    stripeCardDeclineCodeFetchError
  ) {
    return <ErrorResults />;
  }

  if (isPaymentElementEnabled) {
    // This scenario should never happen, since we're either loading or have an error
    // but in local development when hot reloading you can sometimes get in this state, so this prevents Stripe errors
    if (!elementOptions) {
      return null;
    }

    return (
      <Elements stripe={stripePromise} options={elementOptions}>
        <PaymentElementForm
          clientSecret={setupIntent.client_secret}
          stripeCardDeclineCodes={stripeCardDeclineCodes}
          {...props}
        />
      </Elements>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <PaymentFormContent
        clientSecret={setupIntent.client_secret}
        stripeCardDeclineCodes={stripeCardDeclineCodes}
        {...props}
      />
    </Elements>
  );
};

export default PaymentForm;
